import _ from 'lodash';
import moment from 'moment';
import pako from 'pako';

import env from '@/config/env';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, isNilOrEmpty, parseCurrencyToNumber, parseJson } from '@/utils/infrastructure';
import { convertToBase64, toDataURL } from '@/utils/infrastructure/file-system';
import CoreFormula from './formula';

const { FormFieldStringType: FFSType, FormFieldType: FFType } = CoreEnums;

/**
 * Normalizes form/API values: converts objects with a `value` property to their primitive (value.value).
 * Use when setting form values (skipKeys = field names that use labelInValue) or when sending to API (skipKeys = []).
 * @param {Object} values - Key-value map (e.g. form values or payload)
 * @param {Object} [options]
 * @param {string[]} [options.skipKeys=[]] - Keys to leave unchanged (e.g. labelInValue field names).
 * @returns {Object} New object with normalized values
 */
export function normalizeOptionValues(values, options = {}) {
  const { skipKeys = [] } = options;
  const out = {};
  Object.keys(values || {}).forEach((key) => {
    let v = values[key];
    if (
      !skipKeys.includes(key) &&
      v != null &&
      typeof v === 'object' &&
      Object.prototype.hasOwnProperty.call(v, 'value')
    ) {
      v = v.value;
    }
    out[key] = v;
  });
  return out;
}

/**
 * Helper function to change a field visibility by linked siblings
 * @param attrName
 * @param attrSibling
 * @param config
 * @param values
 * @returns {{[p: string]: *}[]}
 */
export function hiddenFieldState({
  attrName,
  attrSibling,
  config,
  values,
  configDefault = {},
  setFields = () => {},
}) {
  let attrSiblings = {};
  if (_.isArray(attrSibling)) {
    attrSiblings = _.find(attrSibling, (l) => {
      return _.isArray(l.value)
        ? _.includes(l.value, values[attrName]?.value) || _.includes(l.value, values[attrName])
        : l.value === values[attrName]?.value || l.value === values[attrName];
    });

    const allHiddenSiblings = _.flatMap(attrSibling, (s) => s.hiddenSiblings || []);

    const fields = [...config.fields].map((f) => {
      let { hidden, rules, value } = f;
      let siblingExits = false;
      const defaultField = _.find(configDefault.fields, { name: f.name });
      const defaultHidden = defaultField?.hidden ?? false;

      if (f.name === attrName) {
        value = values[attrName];
      }

      if (attrSiblings && _.includes(attrSiblings.siblings, f.name)) {
        hidden = !attrSiblings.value;
        siblingExits = true;
        rules = defaultField?.rules || [];
      }

      if (attrSiblings && _.includes(attrSiblings.hiddenSiblings, f.name)) {
        hidden = true;
        rules = [
          {
            required: false,
          },
        ];
        siblingExits = true;
        value = f.type === 'switch' ? f.defaultChecked : null;
      }

      if (!attrSiblings && _.includes(allHiddenSiblings, f.name)) {
        hidden = defaultHidden;
        siblingExits = true;
        rules = defaultField?.rules || [];
        if (!hidden) {
          value =
            defaultField?.defaultValue !== undefined
              ? defaultField.defaultValue
              : defaultField?.value !== undefined
              ? defaultField.value
              : f.type === 'switch'
              ? f.defaultChecked
              : value;
        }
      }

      if (siblingExits) {
        setFields([
          {
            name: f.name,
            value,
          },
        ]);
      }

      return { ...f, hidden, rules, value };
    });

    return fields;
  }

  const index = _.findIndex(config.fields, { name: attrSibling });
  const field = _.find(configDefault.fields, { name: attrSibling });

  const fields = [...config.fields].map((f) => {
    return {
      ...f,
      ...(f.type === 'switch'
        ? { checked: f.name === attrName ? values[attrName] : values[f.name] }
        : { initialValue: values[f.name] }),
    };
  });

  if (_.hasIn(fields[index], 'hidden')) fields[index].hidden = !values[attrName];
  if (_.hasIn(fields[index], 'disabled')) fields[index].disabled = !values[attrName];

  setFields([
    {
      name: attrSibling,
      value: field ? field.value : null,
    },
  ]);

  return fields;
}
/**
 * Helper function to change a field visibility by linked siblings
 * @param attrName
 * @param attrSibling
 * @param config
 * @param values
 * @returns {{[p: string]: *}[]}
 */
export function hiddenFieldStateRules({
  attrName,
  attrSibling,
  config,
  values,
  configDefault = {},
  setFields = () => {},
  edit,
}) {
  let attrSiblings = {};
  if (_.isArray(attrSibling)) {
    attrSiblings = _.find(attrSibling, (l) => l.value === values[attrName]);

    const fields = [...config.fields].map((f) => {
      let { hidden, value } = f;
      let siblingExits = false;

      if (attrSiblings && _.includes(attrSiblings.siblings, f.name)) {
        hidden = !attrSiblings.value;
        siblingExits = true;
      }

      if (attrSiblings && _.includes(attrSiblings.hiddenSiblings, f.name)) {
        hidden = true;
        siblingExits = true;
        value = '';
      }

      if (siblingExits) {
        setFields([
          {
            name: f.name,
            value,
          },
        ]);
      }

      return {
        ...f,
        hidden,
        value,
        rules: f.hidden
          ? _.map(f.rules, (it) => {
              if (_.has(it, 'required')) {
                _.set(it, 'required', false);
              }
              return it;
            })
          : _.map(f.rules, (it) => {
              if (_.has(it, 'required')) {
                _.set(
                  it,
                  'required',
                  edit
                    ? f.requiredEdit
                      ? f.requireddit
                      : f.requiredDefault
                    : f.requiredDefault
                    ? f.requiredDefault
                    : false,
                );
              }

              return it;
            }),
      };
    });

    return fields;
  }

  const index = _.findIndex(config.fields, { name: attrSibling });
  const field = _.find(configDefault.fields, { name: attrSibling });

  const fields = [...config.fields].map((f) => {
    return {
      ...f,
      ...(f.type === 'switch'
        ? {
            checked: f.name === attrName ? values[attrName] : values[f.name],
            defaultChecked: f.name === attrName ? values[attrName] : values[f.name],
          }
        : { initialValue: values[f.name] }),
    };
  });

  if (_.hasIn(fields[index], 'hidden')) fields[index].hidden = !values[attrName];
  if (_.hasIn(fields[index], 'disabled')) fields[index].disabled = !values[attrName];
  if (_.hasIn(fields[index], 'rules')) {
    fields[index].rules = fields[index].hidden
      ? _.map(fields[index].rules, (it) => {
          if (_.has(it, 'required')) {
            _.set(it, 'required', false);
          }
          return it;
        })
      : _.map(fields[index].rules, (it) => {
          if (_.has(it, 'required')) {
            _.set(
              it,
              'required',
              edit
                ? _.has(fields[index], 'requiredEdit')
                  ? fields[index].requiredEdit
                  : it.required
                : _.has(fields[index], 'requiredDefault')
                ? fields[index].requiredDefault
                : it.required,
            );
          }

          return it;
        });
  }

  setFields([
    {
      name: attrSibling,
      value: field ? field.value : '',
    },
  ]);

  return fields;
}

/**
 * Helper function to change a field visibility by linked siblings edit
 * @param fields
 * @returns {{[p: string]: *}[]}
 */
export function hiddenFieldStateEdit(fields) {
  const siblings = _.filter(fields, (f) => f.sibling);
  const resp = _.map(fields, (f) => {
    const field = _.find(siblings, (l) => {
      if (_.isArray(l.sibling)) {
        const fieldsShow = _.find(l.sibling, { value: l.value });
        return fieldsShow && fieldsShow.siblings && _.includes(fieldsShow.siblings, f.name);
      }
      return l.sibling === f.name;
    });

    if (field) {
      return {
        ...f,
        hidden: !field.value,
      };
    }
    return f;
  });
  return _.map(resp, (l) => {
    return {
      ...l,
      rules: l.hidden
        ? [
            {
              required: false,
            },
          ]
        : l.rules,
    };
  });
}

export function hiddenFieldStateAddOrEdit(fields, values, edit) {
  const siblings = _.filter(fields, (f) => f.sibling);
  const resp = _.map(fields, (f) => {
    // Check if this field is controlled by a sibling
    let matchedSibling = null;
    let controllingField = null;

    for (const siblingField of siblings) {
      if (_.isArray(siblingField.sibling)) {
        const matched = _.find(siblingField.sibling, (si) => {
          return _.isArray(si.value)
            ? _.includes(si.value, values[siblingField.name])
            : si.value === values[siblingField.name];
        });

        if (matched) {
          if (matched.siblings && _.includes(matched.siblings, f.name)) {
            matchedSibling = matched;
            controllingField = siblingField;
            break;
          }
          if (matched.hiddenSiblings && _.includes(matched.hiddenSiblings, f.name)) {
            matchedSibling = matched;
            controllingField = siblingField;
            break;
          }
        }
      } else if (siblingField.sibling === f.name) {
        controllingField = siblingField;
        break;
      }
    }

    if (controllingField) {
      if (matchedSibling) {
        // Handle siblings (show) or hiddenSiblings (hide)
        if (matchedSibling.siblings && _.includes(matchedSibling.siblings, f.name)) {
          // Field should be shown
          return {
            ...f,
            hidden: false,
            rules: _.map(f.rules, (it) => {
              if (_.has(it, 'required')) {
                _.set(
                  it,
                  'required',
                  edit
                    ? _.has(f, 'requiredEdit')
                      ? f.requiredEdit
                      : f.required
                    : _.has(f, 'requiredDefault')
                    ? f.requiredDefault
                    : f.required,
                );
              }
              return it;
            }),
          };
        }
        if (matchedSibling.hiddenSiblings && _.includes(matchedSibling.hiddenSiblings, f.name)) {
          // Field should be hidden
          return {
            ...f,
            hidden: true,
            rules: _.map(f.rules, (it) => {
              if (_.has(it, 'required')) {
                _.set(it, 'required', false);
              }
              return it;
            }),
          };
        }
      } else {
        // Simple sibling relationship (old behavior)
        const hasValue =
          values[controllingField.name] !== undefined && values[controllingField.name] !== null;
        return {
          ...f,
          hidden: !hasValue,
          rules: !hasValue
            ? _.map(f.rules, (it) => {
                if (_.has(it, 'required')) {
                  _.set(it, 'required', false);
                }
                return it;
              })
            : _.map(f.rules, (it) => {
                if (_.has(it, 'required')) {
                  _.set(
                    it,
                    'required',
                    edit
                      ? _.has(f, 'requiredEdit')
                        ? f.requiredEdit
                        : f.required
                      : _.has(f, 'requiredDefault')
                      ? f.requiredDefault
                      : f.required,
                  );
                }
                return it;
              }),
        };
      }
    }
    return f;
  });
  return resp;
}

/**
 * Helper function to check when the form is valid
 * @param config Form config
 * @param form Form Instance
 * @returns Boolean
 */
export function checkFormState(config, form, values = {}) {
  const data = { ...form?.getFieldsValue(), ...values };

  const required =
    config?.fields?.filter((o) => !o.hidden && o.rules?.some((r) => r.required)) ?? [];
  const isEmpty = required.some((o) => {
    const item = data[o.name || o.internalName];

    // Non-empty arrays (EntitySelector, multi-select) are valid.
    if (Array.isArray(item)) return item.length === 0;
    if (item?.attachments?.length === 0) return true;
    // Moment / Date from datepicker — lodash `isEmpty` treats them as empty
    // because they have no enumerable own keys.
    if (moment.isMoment?.(item) || item?._isAMomentObject) return false;
    if (item instanceof Date && !Number.isNaN(item.getTime())) return false;
    if (typeof item === 'object' && item !== null) {
      return _.isEmpty(item) || Object.values(item).every((v) => isNilOrEmpty(v));
    }

    return isNilOrEmpty(item);
  });

  if (isEmpty) return false;

  const errors = _.map(data, 'errors')
    .reduce((lhs, rhs) => lhs.concat(rhs), [])
    .filter(Boolean);

  if (errors.length > 0) return false;
  return true;
}

const matchesPattern = (value, pattern) => {
  const regexPattern = pattern
    .replace(/\./g, '\\.') // Escape dots
    .replace(/#/g, '\\d'); // Replace # with digit matcher

  return new RegExp(`^${regexPattern}$`).test(value);
};

/**
 * Helper function to change a field visibility by authorization Type
 * @param config Form config
 * @param type Authorization Type
 * @returns {{[p: string]: *}[]}
 */
export const setFieldFormAuthorizationType = (config, type = 0) => {
  const { fields } = config;
  const [fieldAuth] = _.filter(fields, { name: 'authorizationType' });
  const [attrSiblings] = _.filter(fieldAuth.sibling, { value: type });

  const newFields = fields.map((f) => {
    if (_.includes(attrSiblings.siblings, f.name)) {
      _.set(f, 'hidden', false);
      _.set(
        f,
        'rules',
        f.rules.map((r) => (_.isNil(r.required) ? r : { required: true })),
      );
    }
    if (_.includes(attrSiblings.hiddenSiblings, f.name)) {
      _.set(f, 'hidden', true);
      _.set(
        f,
        'rules',
        f.rules.map((r) => (_.isNil(r.required) ? r : { required: false })),
      );
    }
    return f;
  });
  return { ...config, fields: newFields };
};

/**
 * Helper function to transform all Form Groups related values
 * @param values Form values
 * @returns object
 */
export function parseFormValues(values, fields = []) {
  try {
    return _(values)
      .omitBy((v, k) => _.isNil(v) || k.includes('_raw_value'))
      .toPairs()
      .map((pair) => {
        const [key, item] = pair;

        const {
          type,
          knownType,
          jsonSelectData,
          isAllowMultipleChoicesEnabled,
          allowMultiple,
          mode,
          ...field
        } =
          _.find(fields, (o) => o.internalName === key || o.key === key || o.dataIndex === key) ||
          {};

        if (
          _.isNil(item) ||
          item === '' ||
          (_.isArray(item) && !item.length) ||
          key?.endsWith('_format')
        ) {
          return null;
        }

        if (item?.isTable) {
          if (item?.value instanceof Uint8Array) {
            const value = JSON.parse(pako.ungzip(item.value, { to: 'string' }));
            return [key, value.map((rec) => parseFormValues(rec, item.fields))];
          }

          return [key, item.value.map((rec) => parseFormValues(rec, item.fields))];
        }

        switch (knownType ?? type) {
          case FFType.SelectorUser:
          case FFType.SelectorPosition:
          case FFType.SelectorUserGroup:
          case FFType.SelectorArea:
          case FFType.WebServiceRemoteSearch:
          case FFType.WebServiceRemoteList:
          case FFType.DataBaseRemoteList:
          case FFType.DataBaseRemoteSearch:

          case FFSType.SelectorUser:
          case FFSType.SelectorPosition:
          case FFSType.SelectorUserGroup:
          case FFSType.SelectorArea:
          case FFSType.WebServiceRemoteSearch:
          case FFSType.WebServiceRemoteList:
          case FFSType.DataBaseRemoteList:
          case FFSType.DataBaseRemoteSearch: {
            const temp = parseJson(item);

            if (temp?.items) return [key, temp];

            const isString = typeof temp === 'string' && !isJsonLike(temp);
            const isArray = Array.isArray(temp);
            const isMultiple =
              isAllowMultipleChoicesEnabled ||
              allowMultiple ||
              mode === 'multiple' ||
              mode === 'tags';

            return [
              key,
              {
                items:
                  (isMultiple && isArray) || isArray
                    ? temp?.map((o) => ({ id: o.value, text: o.label, searchValue: o.searchValue }))
                    : [
                        {
                          id: isString ? temp : temp?.value,
                          text: isString ? temp : temp?.label,
                          searchValue: isString ? temp : temp?.searchValue,
                        },
                      ],
              },
            ];
          }

          case FFType.FieldDynamicEntityList:
          case FFType.FieldDynamicEntitySearch:
          case FFType.Choice:

          case FFSType.FieldDynamicEntityList:
          case FFSType.FieldDynamicEntitySearch:
          case FFSType.Choice: {
            if (isAllowMultipleChoicesEnabled || allowMultiple || mode === 'multiple') {
              const tmp = _.some(item, _.isObject) ? _.map(item, 'value') : item;
              return [key, _.isArray(tmp) ? tmp.join(';') : tmp];
            }

            if (
              type === FFSType.FieldDynamicEntityList ||
              type === FFType.FieldDynamicEntityList ||
              type === FFSType.FieldDynamicEntitySearch ||
              type === FFType.FieldDynamicEntitySearch
            ) {
              const temp = item?.value || item;
              const date = moment(temp, CoreFormula._TryGuessDateFormat(temp), true);
              if (isNaN(temp) && date.isValid()) {
                return [
                  key,
                  date.format(
                    CoreFormula.isDateOnly(date)
                      ? env.DATE_PARSE_FORMAT
                      : env.DATE_TIME_PARSE_FORMAT,
                  ),
                ];
              }
            }

            return [key, item.value || item];
          }

          case FFSType.Attachment:
          case FFSType.Signature:
          case FFSType.Image:
          case FFSType.Photo:
          case FFType.Attachment:
          case FFType.Signature:
          case FFType.Image:
          case FFType.Photo: {
            const omitted = [
              'file',
              'fileList',
              'isBase64',
              'fileTypeClassification',
              'created',
              'creator',
            ];

            const { attachments = [] } = isJsonLike(item) ? JSON.parse(item) : item;

            const files = _.map(attachments, (o) => {
              const attach = { ..._.omit(o, omitted) };

              if (o.downloadUrl) {
                toDataURL(o.downloadUrl)
                  .then(convertToBase64)
                  .then((str) => {
                    _.set(attach, 'content', str);
                  });
              }

              return attach;
            });

            return [key, { attachments: files }];
          }

          case FFSType.Time:
          case FFSType.Time: {
            if (moment.isMoment(item)) {
              return [key, item.format('HH:mm')];
            }
            return [key, item];
          }

          case FFSType.Date:
          case FFSType.DateTime:
          case FFType.Date:
          case FFType.DateTime: {
            const format = {
              [FFSType.Date]: env.DATE_PARSE_FORMAT,
              [FFSType.DateTime]: env.DATE_TIME_PARSE_FORMAT,

              [FFType.Date]: env.DATE_PARSE_FORMAT,
              [FFType.DateTime]: env.DATE_TIME_PARSE_FORMAT,
            }[type];

            const date = _.isString(item) ? moment(item) : item;
            return [key, date.format(format)];
          }

          case FFSType.LikertScale:
          case FFType.LikertScale: {
            if (isAllowMultipleChoicesEnabled || allowMultiple) {
              const items = Object.values(item).reduce((lhs, rhs) => lhs.concat(rhs), []);
              return [key, { items }];
            }

            return pair;
          }

          case FFSType.Int:
          case FFType.Int:
          case FFType.Decimal:
          case FFSType.Decimal: {
            return [key, values?.[`${key}_raw_value`] ?? item];
          }

          case FFSType.CheckGroup:
          case FFType.CheckGroup: {
            return [key, _.isArray(item) ? item?.join?.(';') : item];
          }

          case FFSType.Location:
          case FFType.Location:
            return [key, item];

          default:
            return pair;
        }
      })
      .filter(Boolean)
      .fromPairs()
      .value();
  } catch (e) {
    console.log('ERR_PARSE_FORM_VALUES', e);
  }
}
